import { useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';

const DEFAULT_TITLE = 'Vidya.ai';
const DEFAULT_OG_TITLE = 'Vidya.ai';
const DEFAULT_OG_DESCRIPTION =
  'AI-powered educational platform for personalized learning';
const DEFAULT_OG_IMAGE = 'https://withvidya.ai/logo512.png';
const DEFAULT_OG_SITE_NAME = 'Vidya.ai';

// Favicon is intentionally NOT swapped per tenant — Vidya's mark stays in the
// browser tab as the always-visible platform anchor (Stripe Checkout pattern).
// Tenant branding dominates the in-app UI; co-branding lives in the tab title
// and the visible chrome.

function setMeta(attr: 'property' | 'name', value: string, content: string): void {
  if (typeof document === 'undefined') return;
  const tag = document.querySelector<HTMLMetaElement>(`meta[${attr}="${value}"]`);
  if (tag && tag.content !== content) tag.content = content;
}

export function useTenantBranding(): void {
  const { tenant } = useTenant();

  useEffect(() => {
    if (typeof document === 'undefined') return;

    document.title = tenant ? `${tenant.name} x Vidya.ai` : DEFAULT_TITLE;

    // Per-tenant share previews on WhatsApp / Slack / Teams / Twitter / etc.
    // og:image prefers cover_image_url (rich 1200×630 hero) over logo_url
    // (icon-sized), falling back to the Vidya default. Description stays
    // generic on subdomains until Institution.description is added to the
    // bootstrap payload.
    const ogTitle = tenant ? `${tenant.name} · Vidya.ai` : DEFAULT_OG_TITLE;
    const ogDescription = tenant
      ? `Sign in to your ${tenant.name} learning portal — powered by Vidya.ai.`
      : DEFAULT_OG_DESCRIPTION;
    const ogImage =
      tenant?.cover_image_url ?? tenant?.logo_url ?? DEFAULT_OG_IMAGE;
    const ogSiteName = tenant?.name ?? DEFAULT_OG_SITE_NAME;

    setMeta('property', 'og:title', ogTitle);
    setMeta('property', 'og:description', ogDescription);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:site_name', ogSiteName);
    setMeta('name', 'twitter:title', ogTitle);
    setMeta('name', 'twitter:description', ogDescription);
    setMeta('name', 'twitter:image', ogImage);
  }, [tenant]);
}
